/**
 * FrictionRecorder.tsx -- Operator-only friction logging banner.
 *
 * PRD SS10: Enables operators to log friction reports via a small inline
 * form in the chrome. Writes to the additive frictionReports table via
 * the friction.record mutation.
 */
import React, { useCallback, useState, type CSSProperties } from "react";
import { Icon } from "../primitives";

export interface FrictionRecorderProps {
  /** Whether the current user is an operator (controls visibility). */
  isOperator: boolean;
  /** Callback to submit a friction report to the backend. */
  onSubmit: (description: string) => Promise<void>;
}

export function FrictionRecorder({
  isOperator,
  onSubmit,
}: FrictionRecorderProps) {
  const [expanded, setExpanded] = useState(false);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = useCallback(async () => {
    const trimmed = description.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setDescription("");
      setSubmitted(true);
      setExpanded(false);
      // Reset submitted feedback after a moment
      setTimeout(() => setSubmitted(false), 3000);
    } finally {
      setSubmitting(false);
    }
  }, [description, submitting, onSubmit]);

  if (!isOperator) return null;

  const triggerStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    border: "1px solid var(--color-surface-murasaki, #E8DDD0)",
    borderRadius: "6px",
    backgroundColor: "transparent",
    fontSize: "12px",
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "var(--font-display)",
    color: "var(--color-surface-sumi, #1A1614)",
    opacity: 0.7,
  };

  const formStyle: CSSProperties = {
    display: "flex",
    gap: "8px",
    alignItems: "flex-start",
    padding: "8px 16px",
    backgroundColor: "var(--color-surface-shiro, #FAF2EA)",
    borderBottom: "1px solid var(--color-surface-murasaki, #E8DDD0)",
    fontFamily: "var(--font-display)",
  };

  const inputStyle: CSSProperties = {
    flex: 1,
    padding: "8px 10px",
    fontSize: "13px",
    border: "1px solid var(--color-surface-murasaki, #E8DDD0)",
    borderRadius: "4px",
    backgroundColor: "#FFFFFF",
    color: "var(--color-surface-sumi, #1A1614)",
    fontFamily: "inherit",
    outline: "none",
  };

  const submitBtnStyle: CSSProperties = {
    padding: "8px 14px",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    backgroundColor: "var(--color-surface-sora, #5CB8DB)",
    color: "#FFFFFF",
    whiteSpace: "nowrap",
  };

  if (!expanded) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <button type="button" style={triggerStyle} onClick={() => setExpanded(true)}>
          {submitted ? "Recorded" : "Report friction"}
        </button>
      </div>
    );
  }

  return (
    <div style={formStyle}>
      <input
        type="text"
        style={inputStyle}
        placeholder="Describe the friction..."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void handleSubmit();
          if (e.key === "Escape") setExpanded(false);
        }}
        autoFocus
        aria-label="Friction description"
      />
      <button
        type="button"
        style={submitBtnStyle}
        onClick={() => void handleSubmit()}
        disabled={submitting || !description.trim()}
      >
        {submitting ? "Sending..." : "Submit"}
      </button>
      <button
        type="button"
        style={{ ...triggerStyle, opacity: 0.5, border: "none" }}
        onClick={() => setExpanded(false)}
        aria-label="Cancel"
      >
        <Icon name="x" size={16} />
      </button>
    </div>
  );
}

export default FrictionRecorder;
